import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../utils/settingsPersistence';
import { ToolType as CoreToolType } from '../core/types';
import { cadEntityToSceneObject } from '../core/scene';
import { useDocumentStore } from './useDocumentStore';
import { useBlockStore } from './commandAuxStores';

/**
 * Application Mode - determines which UI mode is active
 */
export type AppMode = 'bim' | '2d' | '3d' | 'sketch';

/**
 * Manage tab dialogs. Opened by MANAGE_* tools via openManageDialog()
 * and rendered centrally by ManageDialogsHost.
 */
export type ManageDialogId =
    | 'units'
    | 'snaps'
    | 'materials'
    | 'objectStyles'
    | 'location'
    | 'phasing'
    | 'designOptions'
    | 'idSelection'
    | 'startingView'
    | 'macros'
    | 'images'
    | 'manageLinks'
    | 'cloudFamily'
    | 'typeProperties'
    | 'visibilityGraphics'
    | 'schedules'
    | 'analyzeLoadCases'
    | 'analyzeEnergySettings'
    | 'analyzeEnergyResults'
    | 'analyzeResults'
    // Precast tab dialogs (Configuration, Fabrication)
    | 'precastConfig'
    | 'precastShopDrawings'
    | 'precastCamExport'
    // Command naming presets (Manage tab)
    | 'commandPresets'
    // CAD dimension/text style managers (dimension/text style dialogs)
    | 'dimStyles'
    // APPLOAD — AutoLISP script library (docs/plans/2d/autocad-pgp-lisp-plan.md)
    | 'appLoad'
    // Parametric constraint layer (Faz 6): parameter table + AUTOCONSTRAIN
    // inference settings.
    | 'parameters'
    | 'autoConstrain'
    // Annotation depth dialogs (Faz 8): FIND/REPLACE, TOLERANCE GD&T,
    // FIELD, ATTDEF, ATTMAN, OBJECTSCALE, SCALELISTEDIT, DATALINK.
    | 'findReplace'
    | 'tolerance'
    | 'field'
    | 'attdef'
    | 'attman'
    | 'objectScale'
    | 'scaleListEdit'
    | 'dataLink'
    // Named VPORTS configurations (Faz 10.5): save/restore/delete/join.
    | 'vports';

/**
 * Collaborate tab dialogs. Opened by COLLAB_* tools via openCollabDialog()
 * and rendered centrally by CollabDialogsHost.
 */
export type CollabDialogId =
    | 'worksets'
    | 'collaborate'
    | 'synchronize'
    | 'relinquish'
    | 'publishSettings'
    | 'cloudModels'
    | 'copyMonitor'
    | 'coordinationReview'
    | 'interference';

/**
 * Target of the Edit Type dialog: the family/type being edited,
 * its category (drives the parameter schema) and the element ids that were
 * selected when the dialog was opened (receives type switches/duplicates).
 */
export interface TypePropertiesTarget {
    family: string;
    type: string;
    category: string;
    elementIds: string[];
}

/**
 * Tool Types available across all modes
 */
export type ToolType = CoreToolType;

/**
 * Entity represents any object in the scene (2D or 3D)
 */
export interface Entity {
    id: string;
    type: string;
    name: string;
    visible: boolean;
    locked: boolean;
    layer: string;
    // Geometry data stored as serializable format
    data: any;
}

/**
 * Layer for organizing entities
 */
export interface Layer {
    id: string;
    name: string;
    color: string;
    visible: boolean;
    locked: boolean;
    order: number;
    /** LAYFRZ: frozen layers stay off but are dropped from regen/snap too. */
    frozen?: boolean;
    /** Layer linetype (BYLAYER fallback): DASHED/CENTER/HIDDEN/DOTTED/... */
    linetype?: string;
    /** Layer lineweight in hundredths of a mm (classic ACAD units). */
    lineweight?: number;
    /** Layer transparency 0-100 (BYLAYER fallback). */
    transparency?: number;
    /** Plot flag (Layer Properties Manager "Plot" column); default true. */
    plottable?: boolean;
    /** Free-form layer description shown in the manager. */
    description?: string;
}

/**
 * View state for the 3D viewport
 */
export interface ViewState {
    camera: {
        position: [number, number, number];
        target: [number, number, number];
        up: [number, number, number];
        fov: number;
        near: number;
        far: number;
    };
    projection: 'perspective' | 'orthographic';
    viewType: '3d' | 'plan';
    viewMode: 'wireframe' | 'shaded' | 'rendered' | 'xray' | 'ghosted' | 'hiddenLine' | 'consistentColors' | 'realistic';
    gridVisible: boolean;
    axesVisible: boolean;
    workPlane: {
        origin: [number, number, number];
        normal: [number, number, number];
        visible: boolean;
    } | null;

    // View control bar toggles
    shadowsEnabled: boolean;
    sunPathVisible: boolean;
    cropView: boolean;
    showCropRegion: boolean;
    /** 3D view orientation locked  */
    viewLocked: boolean;
    /** Temporary Hide/Isolate mode: null = off */
    hideIsolateMode: 'isolate' | 'hide' | null;
    /** Category scope for temporary isolate (null = selection-based) */
    isolateCategoryName: string | null;
    /** Elements hidden in this view ("Hide in View"); revealed highlighted in Reveal mode */
    hiddenElementIds: string[];
    /** Reveal Hidden Elements mode */
    revealHidden: boolean;
    /** Thin Lines mode: model/annotation line weights are drawn thin */
    thinLines: boolean;
    /** Massing & Site: show mass forms and their mass floor plates  */
    showMassFormAndFloors: boolean;
}

/**
 * Undo/Redo action record
 */
interface HistoryAction {
    type: string;
    timestamp: number;
    data: unknown;
    undo: () => void;
    redo: () => void;
    /** Optional human label (OCS history_dropdown_labels parity) shown in
     *  the QAT undo history dropdown instead of the raw type name. */
    label?: string;
    /** Rough memory footprint of the captured snapshots (JSON-length
     *  estimate). Ceiling budget only — accuracy is not required. */
    estimatedBytes?: number;
}

/** Undo stack depth (OCS DEFAULT_HISTORY_MAX_ENTRIES). */
export const HISTORY_MAX_ENTRIES = 256;
/** Snapshot memory ceiling for the undo stack (OCS DEFAULT_HISTORY_MAX_BYTES). */
export const HISTORY_MAX_BYTES = 256 * 1024 * 1024;
/** Fallback per-entry estimate when a pusher does not measure its payload. */
const DEFAULT_HISTORY_ENTRY_BYTES = 4096;

function historyEntryBytes(action: HistoryAction): number {
    return action.estimatedBytes ?? DEFAULT_HISTORY_ENTRY_BYTES;
}

/**
 * UNDO Begin/End group buffer (module-level on purpose: transient capture
 * state, never persisted, no re-render fan-out). While a group is open,
 * pushHistory parks actions here; endHistoryGroup composes them into one
 * stack entry (OCS pending-capture semantics).
 */
let historyGroupDepth = 0;
let historyGroupBuffer: HistoryAction[] | null = null;

/**
 * Main application state
 */
export interface AppState {
    // Mode & Tool
    mode: AppMode;
    activeTool: ToolType;
    /** The tool that was active just before the current one (right-click repeat target). */
    lastUsedTool: ToolType;

    // Selection
    selectedIds: string[];
    hoveredId: string | null;
    /**
     * status-bar label for the element currently targeted by the
     * cursor or the Tab-cycle preview ("Basic Wall : Generic - 200mm").
     */
    statusText: string | null;

    // Viewport cursor (hover-driven override, e.g. 'move' over elements)
    cursor: string;

    // Selection Filter Options
    selectionOptions: {
        selectPinned: boolean;
        selectUnderlay: boolean;
        selectLinks: boolean;
        selectFace: boolean;
        dragElementOnSelection: boolean;
    };

    // Entities & Layers
    entities: Record<string, Entity>;
    /** View id of the drawing being edited in the 2D workbench (independent of the BIM active view). null = classic model space. */
    activeDrawingViewId: string | null;
    layers: Record<string, Layer>;
    activeLayerId: string;

    // View
    view: ViewState;
    homeView: ViewState;

    // UI State
    sidebarOpen: boolean;
    propertiesPanelOpen: boolean;
    /** Layer Properties Manager palette presence (Faz 5.1, mirrored both ways
        by SidePanelDockBridge). */
    layerManagerOpen: boolean;
    /** Blocks palette presence (Faz 5.1). */
    blocksOpen: boolean;
    /** External References palette presence (Faz 5.10). */
    xrefOpen: boolean;
    /** Count palette presence (Faz 5.9). */
    countOpen: boolean;
    /** Sheet Set Manager palette presence (Faz 10.13). */
    sheetSetOpen: boolean;
    /** Quick Properties mini palette presence (Faz 5.11, status-bar pill). */
    quickPropertiesOpen: boolean;
    commandLineOpen: boolean;
    /** Resizable command line panel height in px (shared across drafting workbenches). */
    commandLineHeight: number;
    isViewCubeHovered: boolean;

    // Persisted preference settings (Settings dialog)
    /** Workspace opened automatically when the app boots to the picker.
     *  'last' reopens the workbench the app was closed in. */
    defaultMode: AppMode | 'last';
    /** Workbench the app was last in (route-mirrored on every navigation,
     *  persisted) — the boot target when defaultMode is 'last'. */
    lastMode: AppMode;
    /** Single Project Mode: hide document tabs and keep one project open at
     *  a time — opening/creating a project replaces the working document
     *  instead of adding a tab (persisted). */
    singleProjectMode: boolean;
    /** Pointer interaction behavior (persisted). */
    interactionOptions: {
        /** Right-click finishes the active command and repeats the last tool when idle; Shift+right-click opens the context menu. */
        rightClickCompletesCommand: boolean;
        /** Dynamic Input: typed text and coordinate/distance entry follow the cursor in the CAD and 3D workbenches. */
        dynamicInput: boolean;
    };
    /** Sketch workbench display preferences (persisted). */
    sketchOptions: {
        /** Highlight the face under the pointer in the Sketch workbench. Off by default. */
        faceHoverHighlight: boolean;
        /** Face hover highlight color (hex). */
        faceHoverColor: string;
        /** Paint a SketchUp-style sky gradient dome behind the Sketch scene. Off by default. */
        skyGradient: boolean;
        /** Sky gradient zenith color (hex). */
        skyColor: string;
        /** Sky gradient horizon color (hex). */
        horizonColor: string;
        /** Sky gradient ground color below the horizon (hex). */
        groundColor: string;
    };

    // Dialogs
    viewCubeOptionsOpen: boolean;
    helpOpen: boolean;
    /** Full Help Center (search, shortcut matrix, feature guide) modal visibility. */
    helpCenterOpen: boolean;
    /** Global Ortho mode (90° cursor lock) shared across drafting workbenches. */
    orthoMode: boolean;
    /** Shift-held temporary ortho override (BIM workbench drafting). */
    shiftOrthoActive: boolean;
    setShiftOrthoActive: (active: boolean) => void;
    /** Currently open Manage tab dialog (null = none) */
    manageDialog: ManageDialogId | null;
    /** Currently open Collaborate tab dialog (null = none) */
    collabDialog: CollabDialogId | null;
    /** Optional string payload for the open collab dialog (e.g. pre-selection) */
    collabDialogPayload: string | null;
    /** Real-time collaboration room dialog (CollabRoomDialog, opened from the
     *  suite topbar actions in BIM mode or the Toolbar button elsewhere). */
    collabRoomOpen: boolean;
    /** Plugin Manager dialog visibility (PluginManagerDialog). */
    pluginManagerOpen: boolean;
    /** Element/type context for the Edit Type dialog */
    typePropertiesTarget: TypePropertiesTarget | null;

    // ViewCube Settings
    viewCubeOptions: {
        showViewCube: boolean;
        showCompass: boolean;
        snapToView: boolean;
        preserveSceneUpright: boolean;
        inverseCompassRotation: boolean;
        opacity: number; // 0-100
        size: number; // percent of the default scale, 50-150 (100 = normal)
        position: 'Top Right' | 'Top Left' | 'Bottom Right' | 'Bottom Left';
    };

    // History
    history: HistoryAction[];
    historyIndex: number;

    // Actions
    setMode: (mode: AppMode) => void;
    setActiveTool: (tool: ToolType) => void;
    setViewCubeHovered: (hovered: boolean) => void;

    select: (ids: string[]) => void;
    addToSelection: (ids: string[]) => void;
    removeFromSelection: (ids: string[]) => void;
    clearSelection: () => void;
    setHovered: (id: string | null) => void;
    setActiveDrawingView: (viewId: string | null) => void;
    setStatusText: (text: string | null) => void;
    setCursor: (cursor: string) => void;

    // Selection Options Actions
    setSelectionOption: (option: keyof AppState['selectionOptions'], value: boolean) => void;

    // Sketch Options Actions
    setSketchOption: <K extends keyof AppState['sketchOptions']>(option: K, value: AppState['sketchOptions'][K]) => void;

    addEntity: (entity: Entity) => void;
    /** Bulk variant for session rehydration (document swap): one write per
        store instead of a fan-out per object; no dirty marking — the swap
        re-registers the document right after. */
    addEntities: (entities: Entity[]) => void;
    updateEntity: (id: string, updates: Partial<Entity>) => void;
    deleteEntity: (id: string) => void;
    /** Bulk variant for session rehydration (document swap). */
    deleteEntities: (ids: string[]) => void;
    deleteSelected: () => void;

    addLayer: (layer: Layer) => void;
    updateLayer: (id: string, updates: Partial<Layer>) => void;
    deleteLayer: (id: string) => void;
    setActiveLayer: (id: string) => void;

    updateView: (updates: Partial<ViewState>) => void;
    setHomeView: (view: ViewState) => void;
    toggleViewFlag: (flag: 'shadowsEnabled' | 'sunPathVisible' | 'cropView' | 'showCropRegion' | 'viewLocked' | 'revealHidden' | 'thinLines') => void;
    /** Cycle Temporary Hide/Isolate mode: off -> isolate -> hide -> off */
    cycleHideIsolate: (mode?: 'isolate' | 'hide' | null) => void;
    /** Isolate all elements of a category (Temporary Hide/Isolate) */
    isolateCategoryInView: (category: string | null) => void;
    /** Hide elements in the current view ("Hide in View") */
    hideElementsInView: (ids: string[]) => void;
    /** Reveal specific previously hidden elements (Manage Links visibility). */
    unhideElementsInView: (ids: string[]) => void;
    /** Show all temporarily hidden/isolated elements again */
    resetTemporaryHideIsolate: () => void;
    toggleSidebar: () => void;
    togglePropertiesPanel: () => void;
    toggleLayerManager: () => void;
    toggleBlocksPalette: () => void;
    toggleXrefPalette: () => void;
    toggleCountPalette: () => void;
    toggleSheetSetPalette: () => void;
    toggleQuickProperties: () => void;
    toggleCommandLine: () => void;
    /** Resize the shared command line panel (clamped by the caller). */
    setCommandLineHeight: (height: number) => void;
    toggleViewCubeOptions: () => void;
    toggleHelp: () => void;
    /** Open/close the Help Center modal (⌘K / Ctrl+K entry point). */
    setHelpCenterOpen: (open: boolean) => void;
    /** Flip the shared Ortho mode used by the CAD/3D drafting tools. */
    toggleOrthoMode: () => void;
    /** Persisted preference setters (Settings dialog). */
    setDefaultMode: (mode: AppMode | 'last') => void;
    setSingleProjectMode: (enabled: boolean) => void;
    setInteractionOption: (option: keyof AppState['interactionOptions'], value: boolean) => void;
    openManageDialog: (id: ManageDialogId) => void;
    closeManageDialog: () => void;
    openCollabDialog: (id: CollabDialogId, payload?: string | null) => void;
    closeCollabDialog: () => void;
    setCollabRoomOpen: (open: boolean) => void;
    setPluginManagerOpen: (open: boolean) => void;
    openTypeProperties: (target: TypePropertiesTarget) => void;
    updateViewCubeOptions: (updates: Partial<AppState['viewCubeOptions']>) => void;

    undo: () => void;
    redo: () => void;
    pushHistory: (action: HistoryAction) => void;
    /** UNDO Mark (OCS push_undo_mark): a keep-empty stack entry that names
     *  a rollback target for UNDO Back. Accepts an optional label shown in
     *  the history dropdown. Ignored while a Begin/End group is open. */
    markHistory: (label?: string) => void;
    /** UNDO Back: roll back to the most recent mark in one user-visible
     *  step (the mark's own entry is a no-op); with no mark present, rolls
     *  back everything. */
    undoBack: () => void;
    /** UNDO Begin: buffer subsequent history pushes; UNDO End composes them
     *  into ONE stack entry (OCS pending-capture group semantics). */
    beginHistoryGroup: () => void;
    endHistoryGroup: () => void;

    // Reset
    reset: () => void;

    // Tool Preview
    toolPreview: any;
    setToolPreview: (preview: any) => void;
}

/**
 * Default view state
 */
const defaultView: ViewState = {
    camera: {
        position: [10, 10, 10],
        target: [0, 0, 0],
        up: [0, 0, 1], // Z-up world convention (core/scene/axes)
        fov: 50,
        near: 0.1,
        far: 10000,
    },
    projection: 'perspective',
    viewType: '3d',
    viewMode: 'shaded',
    gridVisible: true,
    axesVisible: true,
    workPlane: null,
    shadowsEnabled: false,
    sunPathVisible: false,
    cropView: false,
    showCropRegion: false,
    viewLocked: false,
    hideIsolateMode: null,
    isolateCategoryName: null,
    hiddenElementIds: [],
    revealHidden: false,
    thinLines: false,
    showMassFormAndFloors: true,
};

/**
 * Default layer
 */
const defaultLayer: Layer = {
    id: 'layer-0',
    name: 'Default',
    color: '#ffffff',
    visible: true,
    locked: false,
    order: 0,
};

/**
 * Initial state
 */
const initialState = {
    mode: 'bim' as AppMode,
    activeTool: 'select' as ToolType,
    lastUsedTool: 'select' as ToolType,
    selectedIds: [],
    hoveredId: null,
    statusText: null,
    cursor: 'default',
    selectionOptions: {
        selectPinned: true,
        selectUnderlay: true,
        selectLinks: true,
        selectFace: true,
        dragElementOnSelection: true,
    },
    entities: {},
    activeDrawingViewId: null,
    layers: { 'layer-0': defaultLayer },
    activeLayerId: 'layer-0',
    view: defaultView,
    homeView: defaultView,
    sidebarOpen: true,
    propertiesPanelOpen: true,
    layerManagerOpen: false,
    blocksOpen: false,
    xrefOpen: false,
    countOpen: false,
    sheetSetOpen: false,
    quickPropertiesOpen: false,
    commandLineOpen: false,
    commandLineHeight: 120,
    isViewCubeHovered: false,
    defaultMode: 'last' as AppMode | 'last',
    lastMode: 'bim' as AppMode,
    singleProjectMode: false,
    interactionOptions: {
        rightClickCompletesCommand: true,
        dynamicInput: true,
    },
    sketchOptions: {
        faceHoverHighlight: false,
        faceHoverColor: '#ffd700',
        skyGradient: false,
        skyColor: '#7cb2e4',
        horizonColor: '#dce8f2',
        groundColor: '#bfbfc6',
    },
    viewCubeOptionsOpen: false,
        helpOpen: false,
        helpCenterOpen: false,
    orthoMode: false,
    shiftOrthoActive: false,
    manageDialog: null,
    collabDialog: null,
    collabDialogPayload: null,
    collabRoomOpen: false,
    pluginManagerOpen: false,
    typePropertiesTarget: null,
    viewCubeOptions: {
        showViewCube: true,
        showCompass: true,
        snapToView: true,
        preserveSceneUpright: true,
        inverseCompassRotation: true,
        opacity: 100,
        size: 100,
        position: 'Bottom Right' as 'Top Right' | 'Top Left' | 'Bottom Right' | 'Bottom Left',
    },
    history: [],
    historyIndex: -1,
    toolPreview: null as any, // Placeholder for tool preview data
};

/**
 * Content equality for tool previews. Preview writers hand the store fresh
 * object/Vector3 instances on every pointer event even when the snapped
 * content is unchanged, so reference equality never holds. Compare shallowly
 * with numeric point comparison (duck-typed x/y/z so store.ts stays
 * three-import-free) and a bounded depth for nested payloads like the
 * screen-space selection box.
 */
function isSamePointLike(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
    const pa = a as Record<string, unknown>;
    const pb = b as Record<string, unknown>;
    return typeof pa.x === 'number' && typeof pa.y === 'number' &&
        typeof pb.x === 'number' && typeof pb.y === 'number' &&
        (pa.z === undefined) === (pb.z === undefined) &&
        pa.x === pb.x && pa.y === pb.y &&
        (pa.z === undefined || pa.z === pb.z);
}

function isSamePreviewValue(a: unknown, b: unknown, depth: number): boolean {
    if (a === b) return true;
    if (depth === 0 || a == null || b == null) return false;
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (!isSamePreviewValue(a[i], b[i], depth - 1)) return false;
        }
        return true;
    }
    if (typeof a === 'object' && typeof b === 'object') {
        const ka = Object.keys(a as object);
        const kb = Object.keys(b as object);
        if (ka.length !== kb.length) return false;
        for (const key of ka) {
            if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
        }
        // Preview payloads are tiny; x/y/z-style fields dominate, so try the
        // cheap point comparison before the generic key walk.
        if (isSamePointLike(a, b)) return true;
        for (const key of ka) {
            if (!isSamePreviewValue((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key], depth - 1)) return false;
        }
        return true;
    }
    return false;
}

function isSameToolPreview(current: unknown, next: unknown): boolean {
    if (current === next) return true;
    if (current == null || next == null) return false;
    return isSamePreviewValue(current, next, 4);
}

// ViewCube size migrated from discrete labels to a numeric percent scale.
// Legacy persisted values are mapped onto it and every value is clamped to
// the slider bounds (50-150, 100 = normal) so stale storage can't break it.
const VIEWCUBE_SIZE_MIN = 50;
const VIEWCUBE_SIZE_MAX = 150;
const LEGACY_VIEWCUBE_SIZES: Record<string, number> = {
    Tiny: 50,
    Small: 75,
    Normal: 100,
    Large: 150,
};

function normalizeViewCubeSize(raw: unknown, fallback: number): number {
    if (typeof raw === 'string' && LEGACY_VIEWCUBE_SIZES[raw] !== undefined) {
        return LEGACY_VIEWCUBE_SIZES[raw];
    }
    const value = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(value)) return fallback;
    return Math.min(VIEWCUBE_SIZE_MAX, Math.max(VIEWCUBE_SIZE_MIN, Math.round(value)));
}

/**
 * Main application store using Zustand with Immer for immutable updates.
 * Settings-related UI state (panels, default mode, interaction/view/selection/
 * ViewCube options) is persisted to localStorage and restored on reload.
 */
export const useAppStore = create<AppState>()(
    persist(
        immer((set, get) => ({
        ...initialState,

        // Mode & Tool
        setMode: (mode) => set((state) => {
            state.mode = mode;
            // Track the last workbench for the 'last' startup preference.
            state.lastMode = mode;
        }),
        setActiveTool: (tool) => set((state) => {
            // Remember the previous non-select tool so the right-click
            // repeat-last-command feature has a target while idle.
            if (state.activeTool !== tool && state.activeTool !== 'select') {
                state.lastUsedTool = state.activeTool;
            }
            state.activeTool = tool;
        }),
        setViewCubeHovered: (hovered) => set((state) => { state.isViewCubeHovered = hovered; }),

        // Manage dialogs
        openManageDialog: (id) => set((state) => { state.manageDialog = id; }),
        closeManageDialog: () => set((state) => { state.manageDialog = null; state.typePropertiesTarget = null; }),
        // Collaborate dialogs
        openCollabDialog: (id, payload = null) => set((state) => { state.collabDialog = id; state.collabDialogPayload = payload; }),
        closeCollabDialog: () => set((state) => { state.collabDialog = null; state.collabDialogPayload = null; }),
        setCollabRoomOpen: (open) => set((state) => { state.collabRoomOpen = open; }),
        setPluginManagerOpen: (open) => set((state) => { state.pluginManagerOpen = open; }),
        openTypeProperties: (target) => set((state) => { state.typePropertiesTarget = target; state.manageDialog = 'typeProperties'; }),

        // Selection
        select: (ids) => set((state) => { state.selectedIds = ids; }),
        addToSelection: (ids) => set((state) => {
            const newIds = ids.filter(id => !state.selectedIds.includes(id));
            state.selectedIds.push(...newIds);
        }),
        removeFromSelection: (ids) => set((state) => {
            state.selectedIds = state.selectedIds.filter(id => !ids.includes(id));
        }),
        clearSelection: () => set((state) => { state.selectedIds = []; }),
        setHovered: (id) => set((state) => { state.hoveredId = id; }),
        setActiveDrawingView: (viewId) => set((state) => {
            state.activeDrawingViewId = viewId;
            state.selectedIds = [];
        }),
        setStatusText: (text) => set((state) => { state.statusText = text; }),
        setCursor: (cursor) => set((state) => { state.cursor = cursor; }),
        setSelectionOption: (option, value) => set((state) => { state.selectionOptions[option] = value; }),
        setSketchOption: (option, value) => set((state) => { state.sketchOptions[option] = value; }),

        // Entities — the CAD workbench owns the rich Entity model, but every
        // mutation is mirrored into the document scene ledger (Phase A2) so
        // other workbenches see the same objects through the SceneObject
        // protocol. NEVER write `entities` outside these actions.
        // Every mutation also marks the open document dirty: the CAD workbench
        // is a first-class document owner, so its edits must feed the save
        // prompt / title asterisk exactly like the BIM kernel's `_refresh`.
        addEntity: (entity) => {
            // Drawing-scope tagging: while the 2D workbench edits a drawing
            // view, new entities belong to that view (paper-space entities
            // keep their own onSheet convention and stay untagged).
            const drawingViewId = get().activeDrawingViewId;
            // Block-editor scope tagging (Faz 5.15): while an isolated Block
            // Editor session is open, new entities are tagged with the block
            // name (the onSheet/viewId convention) so EntityLayer renders
            // only the working set.
            const editingBlock = useBlockStore.getState().editingBlock;
            let stored = entity;
            if (drawingViewId && !(entity.data as Record<string, unknown> | undefined)?.onSheet) {
                stored = {
                    ...entity,
                    data: { ...entity.data, viewId: drawingViewId },
                };
            }
            if (editingBlock && !(stored.data as Record<string, unknown> | undefined)?.blockScope) {
                stored = {
                    ...stored,
                    data: { ...stored.data, blockScope: editingBlock.name },
                };
            }
            set((state) => {
                state.entities[stored.id] = stored;
            });
            useDocumentStore.getState().registerSceneObject(cadEntityToSceneObject(stored));
            useDocumentStore.getState().markDirty();
        },
        updateEntity: (id, updates) => {
            set((state) => {
                if (state.entities[id]) {
                    Object.assign(state.entities[id], updates);
                }
            });
            const merged = get().entities[id];
            if (merged) {
                useDocumentStore.getState().registerSceneObject(cadEntityToSceneObject(merged));
                useDocumentStore.getState().markDirty();
            }
        },
        deleteEntity: (id) => {
            set((state) => {
                delete state.entities[id];
                state.selectedIds = state.selectedIds.filter(sid => sid !== id);
            });
            useDocumentStore.getState().removeSceneObject(id, 'cad');
            useDocumentStore.getState().markDirty();
        },
        addEntities: (entities) => {
            // Bulk addEntity (session rehydration): one entities write + one
            // ledger upsert instead of a fan-out per object.
            const drawingViewId = get().activeDrawingViewId;
            const stored = entities.map((entity) => (
                drawingViewId && !(entity.data as Record<string, unknown> | undefined)?.onSheet
                    ? { ...entity, data: { ...entity.data, viewId: drawingViewId } }
                    : entity
            ));
            set((state) => {
                for (const entity of stored) {
                    state.entities[entity.id] = entity;
                }
            });
            useDocumentStore.getState().upsertSceneObjects(stored.map(cadEntityToSceneObject));
        },
        deleteEntities: (ids) => {
            if (ids.length === 0) return;
            const doomed = new Set(ids);
            set((state) => {
                for (const id of doomed) {
                    delete state.entities[id];
                }
                if (state.selectedIds.some(sid => doomed.has(sid))) {
                    state.selectedIds = state.selectedIds.filter(sid => !doomed.has(sid));
                }
            });
            useDocumentStore.getState().removeSceneObjects(ids, 'cad');
        },
        deleteSelected: () => {
            const ids = get().selectedIds;
            set((state) => {
                state.selectedIds.forEach(id => {
                    delete state.entities[id];
                });
                state.selectedIds = [];
            });
            ids.forEach(id => useDocumentStore.getState().removeSceneObject(id, 'cad'));
            if (ids.length > 0) useDocumentStore.getState().markDirty();
        },

        // Layers — part of the saved document (cadScene envelope + legacy
        // saves), so structural changes dirty the document too. View-only
        // changes (camera, display flags) intentionally do NOT: they are
        // session state, mirroring classic BIM behavior.
        addLayer: (layer) => {
            set((state) => {
                state.layers[layer.id] = layer;
            });
            useDocumentStore.getState().markDirty();
        },
        updateLayer: (id, updates) => {
            let changed = false;
            set((state) => {
                if (state.layers[id]) {
                    Object.assign(state.layers[id], updates);
                    changed = true;
                }
            });
            if (changed) useDocumentStore.getState().markDirty();
        },
        deleteLayer: (id) => {
            let deleted = false;
            set((state) => {
                if (Object.keys(state.layers).length > 1 && id !== 'layer-0') {
                    delete state.layers[id];
                    if (state.activeLayerId === id) {
                        state.activeLayerId = 'layer-0';
                    }
                    deleted = true;
                }
            });
            if (deleted) useDocumentStore.getState().markDirty();
        },
        setActiveLayer: (id) => set((state) => { state.activeLayerId = id; }),

        // View
        updateView: (updates) => set((state) => {
            Object.assign(state.view, updates);
        }),
        toggleViewFlag: (flag) => set((state) => {
            state.view[flag] = !state.view[flag];
        }),
        cycleHideIsolate: (mode) => set((state) => {
            const current = state.view.hideIsolateMode;
            if (mode !== undefined) {
                state.view.hideIsolateMode = mode;
            } else {
                state.view.hideIsolateMode = current === null ? 'isolate' : current === 'isolate' ? 'hide' : null;
            }
            if (state.view.hideIsolateMode === null) {
                state.view.isolateCategoryName = null;
            }
        }),
        isolateCategoryInView: (category) => set((state) => {
            state.view.isolateCategoryName = category;
            state.view.hideIsolateMode = category ? 'isolate' : null;
        }),
        hideElementsInView: (ids) => set((state) => {
            const next = new Set(state.view.hiddenElementIds);
            ids.forEach((id) => next.add(id));
            state.view.hiddenElementIds = Array.from(next);
        }),
        unhideElementsInView: (ids) => set((state) => {
            const drop = new Set(ids);
            state.view.hiddenElementIds = state.view.hiddenElementIds.filter((id) => !drop.has(id));
        }),
        resetTemporaryHideIsolate: () => set((state) => {
            state.view.hideIsolateMode = null;
            state.view.isolateCategoryName = null;
            state.view.hiddenElementIds = [];
            state.view.revealHidden = false;
        }),
        setHomeView: (view: ViewState) => set((state) => {
            state.homeView = view;
        }),

        // UI
        toggleSidebar: () => set((state) => { state.sidebarOpen = !state.sidebarOpen; }),
        togglePropertiesPanel: () => set((state) => { state.propertiesPanelOpen = !state.propertiesPanelOpen; }),
        toggleLayerManager: () => set((state) => { state.layerManagerOpen = !state.layerManagerOpen; }),
        toggleBlocksPalette: () => set((state) => { state.blocksOpen = !state.blocksOpen; }),
        toggleXrefPalette: () => set((state) => { state.xrefOpen = !state.xrefOpen; }),
        toggleCountPalette: () => set((state) => { state.countOpen = !state.countOpen; }),
        toggleSheetSetPalette: () => set((state) => { state.sheetSetOpen = !state.sheetSetOpen; }),
        toggleQuickProperties: () => set((state) => { state.quickPropertiesOpen = !state.quickPropertiesOpen; }),
        toggleCommandLine: () => set((state) => { state.commandLineOpen = !state.commandLineOpen; }),
        setCommandLineHeight: (height) => set((state) => { state.commandLineHeight = height; }),
        toggleViewCubeOptions: () => set((state) => { state.viewCubeOptionsOpen = !state.viewCubeOptionsOpen; }),
        toggleHelp: () => set((state) => { state.helpOpen = !state.helpOpen; }),
        setHelpCenterOpen: (open) => set((state) => { state.helpCenterOpen = open; }),
        toggleOrthoMode: () => set((state) => { state.orthoMode = !state.orthoMode; }),
    setShiftOrthoActive: (active) => set((state) => { state.shiftOrthoActive = active; }),
        setDefaultMode: (mode) => set((state) => { state.defaultMode = mode; }),
        setSingleProjectMode: (enabled) => set((state) => { state.singleProjectMode = enabled; }),
        setInteractionOption: (option, value) => set((state) => { state.interactionOptions[option] = value; }),
        updateViewCubeOptions: (updates) => set((state) => {
            Object.assign(state.viewCubeOptions, updates);
        }),

        // History (Undo/Redo)
        undo: () => {
            const { history, historyIndex } = get();
            if (historyIndex >= 0) {
                history[historyIndex].undo();
                set((state) => { state.historyIndex--; });
            }
        },
        redo: () => {
            const { history, historyIndex } = get();
            if (historyIndex < history.length - 1) {
                history[historyIndex + 1].redo();
                set((state) => { state.historyIndex++; });
            }
        },
        pushHistory: (action) => {
            // Open Begin/End group: park the action; endHistoryGroup composes
            // the buffered mutations into ONE stack entry.
            if (historyGroupDepth > 0 && historyGroupBuffer) {
                historyGroupBuffer.push(action);
                return;
            }
            set((state) => {
                // Remove any redo history
                state.history = state.history.slice(0, state.historyIndex + 1);
                state.history.push(action);
                state.historyIndex = state.history.length - 1;

                // Trim (Faz 10.3, OCS trim_history): FIFO over the 256-entry
                // depth and the ~256MB snapshot budget. Entries are closures
                // over already-captured data, so dropped steps cost nothing to
                // release (no deep copies linger).
                let bytes = 0;
                for (const entry of state.history) bytes += historyEntryBytes(entry);
                while (
                    state.history.length > 1
                    && (state.history.length > HISTORY_MAX_ENTRIES || bytes > HISTORY_MAX_BYTES)
                ) {
                    const dropped = state.history.shift();
                    bytes -= historyEntryBytes(dropped!);
                    state.historyIndex--;
                }
            });
        },
        markHistory: (label) => {
            // Marks inside an open group would straddle the composed entry;
            // OCS groups capture mutations only. Silently skipped.
            if (historyGroupDepth > 0) return;
            get().pushHistory({
                type: 'history.mark',
                timestamp: Date.now(),
                data: { label: label ?? null },
                label,
                // Keep-empty step (OCS keep_empty: true): undoing a mark is
                // a no-op — it only names a rollback position.
                undo: () => { /* mark: no state change */ },
                redo: () => { /* mark: no state change */ },
            });
        },
        undoBack: () => {
            const { history, historyIndex } = get();
            if (historyIndex < 0) return;
            let markIdx = -1;
            for (let i = historyIndex; i >= 0; i--) {
                if (history[i].type === 'history.mark') { markIdx = i; break; }
            }
            // Without any mark, Back rolls everything back (AutoCAD asks;
            // the command-line contract here is single-shot, so we go).
            for (let i = historyIndex; i > markIdx; i--) {
                history[i].undo();
            }
            set((state) => { state.historyIndex = markIdx; });
        },
        beginHistoryGroup: () => {
            historyGroupDepth++;
            if (!historyGroupBuffer) historyGroupBuffer = [];
        },
        endHistoryGroup: () => {
            if (historyGroupDepth === 0) return;
            historyGroupDepth--;
            if (historyGroupDepth > 0 || !historyGroupBuffer) return;
            const actions = historyGroupBuffer;
            historyGroupBuffer = null;
            if (actions.length === 0) return;
            get().pushHistory({
                type: 'history.group',
                timestamp: Date.now(),
                data: { count: actions.length },
                label: actions.find((a) => a.label)?.label,
                estimatedBytes: actions.reduce((sum, a) => sum + historyEntryBytes(a), 0),
                undo: () => { for (let i = actions.length - 1; i >= 0; i--) actions[i].undo(); },
                redo: () => { for (const a of actions) a.redo(); },
            });
        },

        // Reset
        reset: () => set(initialState),

        setToolPreview: (preview) => set((state) => {
            // Pointer-move storms write the preview dozens of times per second,
            // usually with identical snapped content (same cell, same anchor).
            // Each write fans out into React re-renders + canvas invalidations,
            // so content-equal writes are dropped here, at the single choke
            // point every class tool flows through.
            if (isSameToolPreview(state.toolPreview, preview)) return;
            state.toolPreview = preview;
        }),
        })),
        {
            name: 'yap-app-settings',
            storage: settingsStorage,
            version: 1,
            // v0 → v1: the startup preference gained a 'last' choice that
            // reopens the last closed workbench. Persisted values equal to
            // the old default ('bim') — plus the pre-rename legacy ids —
            // can't be told apart from an explicit pin, so they migrate to
            // 'last'; explicitly chosen '2d'/'3d'/'sketch' keep their pin.
            migrate: (persisted) => {
                const p = (persisted ?? {}) as { defaultMode?: string };
                const LEGACY_DEFAULTS = ['bim', 'unified', 'revit'];
                if (p.defaultMode === undefined || LEGACY_DEFAULTS.includes(p.defaultMode)) {
                    return { ...p, defaultMode: 'last' };
                }
                return p;
            },
            // Persist only user-preference slices. Transient session state
            // (entities, selection, history, tool preview, dialogs) must NOT
            // be restored on reload.
            partialize: (state) => ({
                sidebarOpen: state.sidebarOpen,
                propertiesPanelOpen: state.propertiesPanelOpen,
                layerManagerOpen: state.layerManagerOpen,
                blocksOpen: state.blocksOpen,
                xrefOpen: state.xrefOpen,
                countOpen: state.countOpen,
                commandLineOpen: state.commandLineOpen,
                commandLineHeight: state.commandLineHeight,
                defaultMode: state.defaultMode,
                lastMode: state.lastMode,
                singleProjectMode: state.singleProjectMode,
                interactionOptions: state.interactionOptions,
                selectionOptions: state.selectionOptions,
                sketchOptions: state.sketchOptions,
                viewCubeOptions: state.viewCubeOptions,
                // Persist the stable display flags from the view state, not the
                // camera position (a rehydrated stale camera fights the
                // initial framing effect).
                view: {
                    viewMode: state.view.viewMode,
                    projection: state.view.projection,
                    gridVisible: state.view.gridVisible,
                    axesVisible: state.view.axesVisible,
                    thinLines: state.view.thinLines,
                    cropView: state.view.cropView,
                    showCropRegion: state.view.showCropRegion,
                    showMassFormAndFloors: state.view.showMassFormAndFloors,
                    camera: { fov: state.view.camera.fov },
                },
            }),
            // Deep-merge persisted settings over the defaults so new fields
            // added in future versions don't come back undefined.
            merge: (persisted, current) => {
                const p = (persisted ?? {}) as Partial<AppState>;
                const legacyDefaultMode = (p as { defaultMode?: string }).defaultMode;
                const LEGACY_MODE_IDS: Record<string, AppMode> = {
                    unified: 'bim',
                    revit: 'bim',
                    autocad: '2d',
                    rhino: '3d',
                };
                return {
                    ...current,
                    ...p,
                    // Legacy persisted defaults: the removed Unified workbench
                    // and the pre-rename mode ids map
                    // onto the neutral workbench ids (bim/2d/3d).
                    defaultMode: (legacyDefaultMode
                        ? (LEGACY_MODE_IDS[legacyDefaultMode] ?? (legacyDefaultMode as AppMode))
                        : current.defaultMode) as AppMode,
                    interactionOptions: { ...current.interactionOptions, ...(p.interactionOptions ?? {}) },
                    selectionOptions: { ...current.selectionOptions, ...(p.selectionOptions ?? {}) },
                    sketchOptions: { ...current.sketchOptions, ...(p.sketchOptions ?? {}) },
                    viewCubeOptions: {
                        ...current.viewCubeOptions,
                        ...(p.viewCubeOptions ?? {}),
                        size: normalizeViewCubeSize(
                            (p.viewCubeOptions as Record<string, unknown> | undefined)?.size,
                            current.viewCubeOptions.size,
                        ),
                    },
                    view: {
                        ...current.view,
                        ...(p.view ?? {}),
                        camera: { ...current.view.camera, ...(p.view?.camera ?? {}) },
                    },
                };
            },
        }
    )
);

/**
 * Selector hooks for optimized re-renders
 */
export const useMode = () => useAppStore((state) => state.mode);
export const useTool = () => useAppStore((state) => state.activeTool);
export const useSelection = () => useAppStore((state) => state.selectedIds);
export const useView = () => useAppStore((state) => state.view);
export const useLayers = () => useAppStore((state) => state.layers);
export const useEntities = () => useAppStore((state) => state.entities);

/**
 * Entities belonging to a 2D drawing view. Entities are tagged with
 * `data.viewId` (parallel to the paper-space `data.onSheet` convention);
 * untagged entities live in classic model space. Pass `viewId = null`
 * (or undefined) to get the model-space set.
 */
export const selectEntitiesForView = (
    entities: Record<string, Entity>,
    viewId: string | null | undefined
): Record<string, Entity> => {
    if (!viewId) return entities;
    const result: Record<string, Entity> = {};
    for (const id in entities) {
        const entity = entities[id];
        if ((entity.data as Record<string, unknown> | undefined)?.viewId === viewId) {
            result[id] = entity;
        }
    }
    return result;
};

/**
 * O(1) selection membership (task 1.3 / F7 fix).
 *
 * Derives a Set from selectedIds, memoized with useShallow so the selector
 * result stays referentially stable across unrelated store updates.
 * Per-element consumers read membership with boolean selectors
 * (e.g. `state.selectedIds.includes(id)`), so only the 2 affected elements
 * re-render on a selection change.
 */
export const useSelectedIdSet = (): Set<string> =>
    useAppStore(useShallow((state) => new Set(state.selectedIds)));
